import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useCookieConsent } from "./useCookieConsent";
import { CollinsUser } from "../types/user";
import { VenueGroupConfig } from "../types/VenueGroup";
import { dmnStorage } from "../utils/dmnStorage";
import { getEnvironment, isBrowser, isNativeTab } from "../utils/environment";
import { reportThirdPartyInitError } from "../telemetry/telemetry";

declare global {
  interface Window {
    Intercom: (
      command: string,
      options?: IntercomSettings | object | string,
      metadata?: Record<string, unknown>
    ) => void;
  }
}

type SignedInVia = "workspace" | "direct link" | "mobile app";

interface IntercomSettings {
  app_id: string;
  user_id?: string;
  companies?: Array<{
    company_id: string;
    name: string;
    created_at?: number;
  }>;
  name?: string;
  email?: string;
  intercom_user_jwt?: string;
  created_at?: number;
  site?: string;
  last_request_at?: number;
  collins_evo_enabled?: boolean;
  evo_enabled?: boolean;
  signed_in_via?: SignedInVia;
  hide_default_launcher?: boolean;
}

interface UseIntercomProps {
  user?: CollinsUser;
  venueGroupConfig?: VenueGroupConfig | null;
  isAuthenticated: boolean;
}

function loadIntercomScript(appId: string): void {
  if (typeof window.Intercom === "function") return;

  const w = window as Window & { Intercom: unknown };
  const i = function (...args: unknown[]) {
    (i as unknown as { q: unknown[] }).q.push(args);
  };
  (i as unknown as { q: unknown[] }).q = [];
  w.Intercom = i as unknown as typeof window.Intercom;

  const script = document.createElement("script");
  script.type = "text/javascript";
  script.async = true;
  script.crossOrigin = "anonymous";
  script.src = `https://widget.intercom.io/widget/${appId}`;
  document.head.appendChild(script);
}

function getSignedInVia(): SignedInVia {
  if (!isBrowser()) {
    return "mobile app";
  }

  if (isNativeTab()) {
    return "workspace";
  }

  return "direct link";
}

export const useIntercom = ({
  user,
  venueGroupConfig,
  isAuthenticated
}: UseIntercomProps): void => {
  const location = useLocation();
  const { consent } = useCookieConsent();
  const hasBooted = useRef(false);
  const hasUpdatedVenueGroup = useRef(false);
  const scriptLoaded = useRef(false);

  useEffect(() => {
    const bootIntercom = async () => {
      if (
        !consent.isResolved ||
        !consent.functional ||
        !isAuthenticated ||
        !user ||
        hasBooted.current
      ) {
        return;
      }

      const environment = await getEnvironment();
      const intercomAppId = environment?.INTERCOM_APP_ID;

      if (!intercomAppId) {
        return;
      }

      if (!scriptLoaded.current) {
        loadIntercomScript(intercomAppId);
        scriptLoaded.current = true;
      }

      if (typeof window.Intercom !== "function") {
        return;
      }

      const companies: Array<{
        company_id: string;
        name: string;
        created_at?: number;
      }> = [];
      if (venueGroupConfig?._id) {
        companies.push({
          company_id: venueGroupConfig._id,
          name: venueGroupConfig.title ?? "",
          created_at: venueGroupConfig.created_date
            ? Math.floor(
                new Date(venueGroupConfig.created_date).getTime() / 1000
              )
            : undefined
        });
        hasUpdatedVenueGroup.current = true;
      }

      const createdAt = user.created_date
        ? Math.floor(new Date(user.created_date).getTime() / 1000)
        : undefined;

      const collinsEvoEnabled = venueGroupConfig?.product_tier === "evo";

      const storedVisibility = await dmnStorage.get("intercomVisible");
      const hideDefaultLauncher = storedVisibility === false;

      window.Intercom("boot", {
        app_id: intercomAppId,
        user_id: user.workspace_id,
        intercom_user_jwt: user.intercom_user_jwt,
        companies,
        name: `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim(),
        email: user.email,
        created_at: createdAt,
        collins_evo_enabled: collinsEvoEnabled,
        evo_enabled: !!venueGroupConfig?.workspace_organisation_id,
        signed_in_via: getSignedInVia(),
        hide_default_launcher: hideDefaultLauncher
      });

      hasBooted.current = true;
    };

    bootIntercom().catch((error: unknown) => {
      console.error("Failed to boot Intercom", error);
      reportThirdPartyInitError("Intercom boot failed", error);
    });
  }, [
    isAuthenticated,
    user,
    venueGroupConfig,
    consent.isResolved,
    consent.functional
  ]);

  useEffect(() => {
    if (
      !hasBooted.current ||
      hasUpdatedVenueGroup.current ||
      !venueGroupConfig?._id ||
      typeof window.Intercom !== "function"
    ) {
      return;
    }

    const companies: Array<{
      company_id: string;
      name: string;
      created_at?: number;
    }> = [];
    companies.push({
      company_id: venueGroupConfig._id,
      name: venueGroupConfig.title ?? "",
      created_at: venueGroupConfig.created_date
        ? Math.floor(new Date(venueGroupConfig.created_date).getTime() / 1000)
        : undefined
    });

    window.Intercom("update", {
      companies,
      collins_evo_enabled: venueGroupConfig.product_tier === "evo",
      evo_enabled: !!venueGroupConfig.workspace_organisation_id
    });

    hasUpdatedVenueGroup.current = true;
  }, [venueGroupConfig]);

  useEffect(() => {
    if (!hasBooted.current || typeof window.Intercom !== "function") {
      return;
    }

    const updateIntercom = async () => {
      const environment = await getEnvironment();
      const intercomAppId = environment?.INTERCOM_APP_ID;

      if (!intercomAppId) {
        return;
      }

      const pathParts = location.pathname.split("/");
      const siteIndex = pathParts.indexOf("sites");
      const venueId =
        siteIndex !== -1 && pathParts.length > siteIndex + 1
          ? pathParts[siteIndex + 1]
          : undefined;

      if (venueId) {
        window.Intercom("update", {
          app_id: intercomAppId,
          user_id: user?.workspace_id,
          site: venueId,
          email: user?.email
        });
      } else {
        window.Intercom("update", {
          last_request_at: Math.floor(Date.now() / 1000)
        });
      }
    };

    updateIntercom();
  }, [location.pathname, user?.workspace_id, user?.email]);

  useEffect(() => {
    if (!consent.isResolved || consent.functional || !hasBooted.current) return;

    if (typeof window.Intercom === "function") {
      window.Intercom("shutdown");
      hasBooted.current = false;
    }
  }, [consent.functional, consent.isResolved]);
};
